import {
  ACCORDION_SECTION_IDS,
  EXPERIENCE_TAGS,
  SECTION_DEEP_LINK_ALIASES,
  SECTION_DEEP_LINK_PARAM
} from './constants';

const getCustomBlindsUrl = ({
  canonicalUrl,
  isBuildAndBuyProduct,
  blindsHost,
  merchQueryParam
}) => {
  const hasMerchQueryParam = merchQueryParam?.startsWith('MERCH=') && (merchQueryParam?.includes('&') === false);
  if (!isBuildAndBuyProduct && hasMerchQueryParam) { return `${canonicalUrl}?${merchQueryParam}`; }

  if (!isBuildAndBuyProduct && !hasMerchQueryParam) { return canonicalUrl; }
  const regex = /\/p\//gi;
  const customBlindsPath = '/p/custom-blinds/';
  const customProductUrl = canonicalUrl && canonicalUrl.replace(regex, customBlindsPath);
  return blindsHost ? `${blindsHost}${customProductUrl}` : canonicalUrl;
};

export const getCustomUrlWithAnalytics = ({
  canonicalUrl,
  isBuildAndBuyProduct,
  blindsHost,
  merchQueryParam
}) => {
  let productURL = getCustomBlindsUrl({ canonicalUrl,
    isBuildAndBuyProduct,
    blindsHost,
    merchQueryParam });
  return productURL;
};

export const smoothScroll = (event, target, offsetselector) => {
  // Guard against SSR - only execute on client
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  const getElementHeight = (element) => {
    const clone = element.cloneNode(true);

    clone.style.position = 'absolute';
    clone.style.visibility = 'hidden';
    clone.style.display = 'block';
    document.body.append(clone);

    const elementHeight = clone.getBoundingClientRect().height;

    clone.remove();
    return elementHeight;
  };

  const getOffset = (offsetSelector) => {
    if (!offsetselector) return null;
    const element = document.querySelector(`${offsetSelector}`);
    return element ? getElementHeight(element) : null;
  };

  const currentPosition = () => {
    return window.scrollY || window.pageYOffset;
  };

  const targetPosition = (element) => {
    const start = currentPosition();
    return element.getBoundingClientRect().top + start;
  };

  event.preventDefault();

  if (target && offsetselector) {
    const targetElement = target ? document.querySelector(`${target}`) : null;

    if (targetElement) {
      const targetLocation = targetPosition(targetElement) - getOffset(offsetselector);

      window.scroll({
        top: targetLocation,
        behavior: 'smooth'
      });
    }
  }
};

/** Accepts a section key (`reviews`) or the wrapper id the accordion renders. */
const SECTION_BY_VALUE = Object.entries(ACCORDION_SECTION_IDS)
  .reduce((acc, [key, id]) => Object.assign(acc, { [key]: key, [id]: key }), Object.create(null));

const ALIAS_PARAMS = Object.keys(SECTION_DEEP_LINK_ALIASES);

const toDeepLink = (section, param, value) => (section
  ? {
    section,
    sectionId: ACCORDION_SECTION_IDS[section],
    param,
    value
  }
  : null);

/**
 * Reads which collapsible section, if any, the URL deep links to.
 *
 * The write-a-review params stay supported so links already in the wild keep landing on
 * the reviews section.
 *
 * @param {string} search - a query string, i.e. `window.location.search`
 * @return {?{section: string, sectionId: string, param: string, value: string}} null when
 * the URL carries no section deep link.
 */
export const getAccordionDeepLink = (search) => {
  const params = new URLSearchParams(search || '');
  const requested = params.get(SECTION_DEEP_LINK_PARAM);
  const alias = ALIAS_PARAMS.find((param) => params.get(param) === 'true');

  return toDeepLink(SECTION_BY_VALUE[requested], SECTION_DEEP_LINK_PARAM, requested)
    || toDeepLink(alias && SECTION_DEEP_LINK_ALIASES[alias], alias, 'true');
};

export const getProductObject = (reviewStats, totalResults) => {
  const { store } = reviewStats?.Includes?.Products || {};
  const reviewCount = store?.FilteredReviewStatistics?.TotalReviewCount;
  if (reviewCount === totalResults) {
    return store || null;
  }
  const itemObject = reviewStats?.Includes?.Products?.items
    ?.find((el) => el?.FilteredReviewStatistics?.TotalReviewCount === totalResults);
  return itemObject || store;
};

export const getRatingDistribution = (totalReviewCount, distribution) => {
  let stats = [{
    ratingValue: 5,
    count: 0,
    ratingPercentage: '0',
  },
  {
    ratingValue: 4,
    count: 0,
    ratingPercentage: '0',
  },
  {
    ratingValue: 3,
    count: 0,
    ratingPercentage: '0',
  },
  {
    ratingValue: 2,
    count: 0,
    ratingPercentage: '0',
  },
  {
    ratingValue: 1,
    count: 0,
    ratingPercentage: '0',
  },
  ];
  return stats.map((element) => {
    const starDistribution = distribution?.filter((el) => el.RatingValue === element.ratingValue)[0];
    if (starDistribution) {
      return {
        ratingValue: starDistribution.RatingValue,
        count: starDistribution.Count,
        ratingPercentage: Math.round((starDistribution.Count / totalReviewCount) * 100).toString(),
      };
    } return element;
  });
};

export const getRecommendationsPercentage = (filteredReviewStatistics) => {
  const recommendedCount = filteredReviewStatistics?.RecommendedCount || 0;
  const notRecommendedCount = filteredReviewStatistics?.NotRecommendedCount || 0;
  const sumRecommended = recommendedCount + notRecommendedCount;
  const recommendationPercentage = sumRecommended ? Math.round((recommendedCount / sumRecommended) * 100) : 0;

  return recommendationPercentage;
};

export const getReviewInfo = (itemObj, product, totalResults) => {
  // const TotalResults = reviewStats?.TotalResults;
  const totalReviews = totalResults || product?.reviews?.ratingsReviews?.totalReviews || 0;
  const reviewCount = typeof TotalResults === 'number' ? totalResults : totalReviews;
  const averageRating = reviewCount === 0 ? 0
    : itemObj?.FilteredReviewStatistics?.AverageOverallRating
      || product?.reviews?.ratingsReviews?.averageRating
      || 0;
  const averageRatingNumber = Number(averageRating);

  let ratingsTitle;
  if (averageRatingNumber === 0) {
    ratingsTitle = 'Write the first Review';
  } else {
    ratingsTitle = `${averageRatingNumber.toFixed(1)} out of 5`;
  }

  return { averageRating: averageRatingNumber, ratingsTitle };

};

/**
 * Adds zero-width spaces after specific characters to improve text wrapping
 * Browsers will treat these invisible characters as valid break points
 * This was added after noticing (on PLP and recs-containers) that browsers don't break lines at
 * certain characters like slashes (/).
 * How it works. For example:
  - Original description: "Blue/Gray-Medium"
    console.log("Blue/Gray-Medium".length); // 15
  - After replacement: "Blue/Gray-Medium"
    console.log(formatTextWithZeroWidthSpace("Blue/Gray-Medium").length); // 17
 * When the product name needs to wrap because it's too long for the line-clamped container:
  - Without zero-width spaces: The browser can't break at slashes/hyphens or other characters
  - With zero-width spaces: The browser sees these as valid break points allowing to properly line-clamp.
 *
 * @param {string} text - The text to format
 * @return {string} - Text with zero-width spaces inserted
 */
export const formatTextWithZeroWidthSpace = (text) => {
  if (!text) return '';

  const specialChars = ['/', '-', '_', '.', '\\', ':', '\'', '"', ')', ']', '>'];

  if (!specialChars.some((char) => text.includes(char))) {
    return text;
  }

  let result = text;
  specialChars.forEach((char) => {
    result = result.split(char).join(char + '\u200B');
  });

  return result;
};

export const doesGiftCardPromoExist = (conditionalPromotions) => {
  if (conditionalPromotions === undefined || conditionalPromotions === null || !Array.isArray(conditionalPromotions)) {
    return false;
  }
  return conditionalPromotions.some(
    (promotion) => promotion?.experienceTag === EXPERIENCE_TAGS.GIFT_CARD
  );
};

export const shouldShowGiftCardBadge = (isGiftCardPromoEnabled, conditionalPromotions, hideGiftCardPromotion) => {
  return isGiftCardPromoEnabled && doesGiftCardPromoExist(conditionalPromotions) && !hideGiftCardPromotion;
};

export const shouldDisplayAnyBadge = ({
  defaultBadge,
  eventBadge,
  colorDisplayBadge,
  shouldDisplayGiftCardBadgePromo
}) => {
  return !!(defaultBadge || eventBadge || colorDisplayBadge || shouldDisplayGiftCardBadgePromo);
};

export const isMobile = (channel) => {
  return channel?.toLowerCase() === 'mobile';
};
